import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { Mail, ArrowLeft, Send, CheckCircle } from 'lucide-react';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post('/api/auth/forgot-password', { email });
      setSubmitted(true);
      toast.success('If an account exists, a password reset link has been sent.');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to send reset link. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const styles = {
    container: {
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 50%, #0f172a 100%)',
      padding: '20px'
    },
    card: {
      backgroundColor: '#ffffff',
      borderRadius: '20px',
      boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
      width: '100%',
      maxWidth: '440px',
      overflow: 'hidden'
    },
    header: {
      background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
      padding: '40px 30px',
      textAlign: 'center'
    },
    iconWrapper: {
      width: '80px',
      height: '80px',
      borderRadius: '50%',
      backgroundColor: 'rgba(255,255,255,0.2)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 20px'
    },
    title: {
      color: '#ffffff',
      fontSize: '26px',
      fontWeight: '700',
      margin: '0 0 8px 0'
    },
    subtitle: {
      color: 'rgba(255,255,255,0.85)',
      fontSize: '15px',
      margin: 0
    },
    body: {
      padding: '35px 30px'
    },
    description: {
      color: '#64748b',
      fontSize: '15px',
      lineHeight: 1.7,
      marginBottom: '25px',
      textAlign: 'center'
    },
    formGroup: {
      marginBottom: '20px'
    },
    label: {
      display: 'block',
      marginBottom: '8px',
      fontSize: '14px',
      fontWeight: '600',
      color: '#374151'
    },
    inputWrapper: {
      position: 'relative'
    },
    inputIcon: {
      position: 'absolute',
      left: '14px',
      top: '50%',
      transform: 'translateY(-50%)',
      color: '#9ca3af'
    },
    input: {
      width: '100%',
      padding: '14px 14px 14px 46px',
      border: '2px solid #e5e7eb',
      borderRadius: '12px',
      fontSize: '15px',
      transition: 'all 0.2s',
      outline: 'none',
      boxSizing: 'border-box'
    },
    button: {
      width: '100%',
      padding: '16px',
      background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
      color: '#ffffff',
      border: 'none',
      borderRadius: '12px',
      fontSize: '16px',
      fontWeight: '600',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '10px',
      transition: 'all 0.2s'
    },
    backLink: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      marginTop: '25px',
      color: '#6b7280',
      textDecoration: 'none',
      fontSize: '14px',
      fontWeight: '500'
    },
    successContainer: {
      textAlign: 'center',
      padding: '20px 0'
    },
    successIcon: {
      width: '80px',
      height: '80px',
      borderRadius: '50%',
      backgroundColor: '#ecfdf5',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 20px'
    },
    successTitle: {
      fontSize: '22px',
      fontWeight: '700',
      color: '#0f172a',
      marginBottom: '12px'
    },
    successText: {
      color: '#64748b',
      fontSize: '15px',
      lineHeight: 1.7,
      marginBottom: '8px'
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.header}>
          <div style={styles.iconWrapper}>
            {submitted ? <CheckCircle size={40} color="#ffffff" /> : <Mail size={40} color="#ffffff" />}
          </div>
          <h1 style={styles.title}>{submitted ? 'Check Your Email' : 'Forgot Password?'}</h1>
          <p style={styles.subtitle}>
            {submitted ? 'We sent you a reset link' : 'No worries, we\'ll help you reset it'}
          </p>
        </div>

        <div style={styles.body}>
          {submitted ? (
            <div style={styles.successContainer}>
              <div style={styles.successIcon}>
                <Mail size={36} color="#10b981" />
              </div>
              <h3 style={styles.successTitle}>Email Sent!</h3>
              <p style={styles.successText}>
                If an account exists for <strong>{email}</strong>, you will receive a password reset link shortly.
              </p>
              <p style={styles.successText}>
                Please check your inbox and spam folder.
              </p>
              <p style={{ ...styles.successText, marginTop: '20px', fontSize: '13px' }}>
                Didn't receive the email?{' '}
                <button 
                  onClick={() => setSubmitted(false)}
                  style={{ background: 'none', border: 'none', color: '#3b82f6', cursor: 'pointer', fontWeight: '600' }}
                >
                  Try again
                </button>
              </p>
            </div>
          ) : (
            <>
              <p style={styles.description}>
                Enter the email address associated with your account, and we'll send you a link to reset your password.
              </p>

              <form onSubmit={handleSubmit}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Email Address</label>
                  <div style={styles.inputWrapper}>
                    <Mail size={20} style={styles.inputIcon} />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your email"
                      required
                      style={styles.input}
                      onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                      onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    ...styles.button,
                    opacity: loading ? 0.7 : 1,
                    cursor: loading ? 'not-allowed' : 'pointer'
                  }}
                >
                  {loading ? (
                    'Sending...'
                  ) : (
                    <>
                      <Send size={20} />
                      Send Reset Link
                    </>
                  )}
                </button>
              </form>
            </>
          )}

          <Link to="/login" style={styles.backLink}>
            <ArrowLeft size={18} />
            Back to Login
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
